import React, { useState, useEffect } from 'react'
import { ChartBarIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { DEFAULT_TXN_RISK_CONFIG } from '../payout-safe/payoutTransactionRisk'

const GROUPS = [
  { title: 'Amount bands (AUD → points)', desc: 'Larger payouts score higher. Thresholds in dollars; points add to total.',
    fields: [
      ['amount_band_low_threshold', 'Low threshold ($)'],
      ['amount_band_low_points', 'Low points'],
      ['amount_band_mid_threshold', 'Mid threshold ($)'],
      ['amount_band_mid_points', 'Mid points'],
      ['amount_band_high_threshold', 'High threshold ($)'],
      ['amount_band_high_points', 'High points'],
    ] },
  { title: 'Cash ratio bands (% → points)', desc: 'Share of the payout paid in cash. Thresholds are percentages.',
    fields: [
      ['cash_ratio_low_threshold', 'Low % threshold'],
      ['cash_ratio_low_points', 'Low points'],
      ['cash_ratio_mid_threshold', 'Mid % threshold'],
      ['cash_ratio_mid_points', 'Mid points'],
      ['cash_ratio_high_threshold', 'High % threshold'],
      ['cash_ratio_high_points', 'High points'],
    ] },
  { title: 'Cash component (desk-cap signal)', desc: 'Cash dollars on a single payout — flags structuring near the desk cap.',
    fields: [
      ['cash_component_cap', 'Desk cap ($)'],
      ['cash_component_cap_points', 'At/above cap points'],
      ['cash_component_near_cap', 'Near cap ($)'],
      ['cash_component_near_cap_points', 'Near-cap points'],
    ] },
  { title: 'Tier & win-type signals', desc: 'PV3/PV4 verification tiers and jackpot wording in game type.',
    fields: [
      ['tier_pv3_points', 'PV3 ($5k–$10k) points'],
      ['tier_pv4_points', 'PV4 ($10k+) points'],
      ['win_type_jackpot_points', 'Jackpot / major prize points'],
    ] },
  { title: 'Cutoffs & investigate trigger', desc: 'Where the score becomes HIGH/MEDIUM, and when "review recommended" fires.',
    fields: [
      ['level_high_threshold', 'HIGH level cutoff (score ≥)'],
      ['level_medium_threshold', 'MEDIUM level cutoff (score ≥)'],
      ['investigate_threshold', '"Suggest investigate" score'],
      ['patron_high_combined_patron_score', 'Combined trigger — patron score ≥'],
      ['patron_high_combined_txn_score', 'Combined trigger — txn score ≥'],
    ] },
]

const ALL_KEYS = GROUPS.flatMap((g) => g.fields.map(([k]) => k))

export default function TransactionRiskConfiguration() {
  const [config, setConfig] = useState(() => {
    const out = {}
    for (const k of ALL_KEYS) out[k] = String(DEFAULT_TXN_RISK_CONFIG[k])
    return out
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venue-config/transaction-risk'))
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const j = await r.json()
        if (cancelled) return
        if (j?.success && j.config) {
          setConfig((prev) => {
            const next = { ...prev }
            for (const k of ALL_KEYS) {
              if (j.config[k] != null) next[k] = String(j.config[k])
            }
            return next
          })
        }
      } catch (e) {
        if (!cancelled) setMessage({ kind: 'error', text: `Could not load saved config: ${e.message}. Showing defaults.` })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }))

  const resetToDefaults = () => {
    const out = {}
    for (const k of ALL_KEYS) out[k] = String(DEFAULT_TXN_RISK_CONFIG[k])
    setConfig(out)
    setMessage({ kind: 'info', text: 'Reset to platform defaults — not saved yet. Click Save to persist.' })
  }

  const save = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const numeric = {}
      for (const k of ALL_KEYS) {
        const n = Number(config[k])
        if (Number.isFinite(n)) numeric[k] = n
      }
      const r = await authFetch(getLocalApiUrl('/api/venue-config/transaction-risk'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: numeric }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const j = await r.json()
      if (!j?.success) throw new Error(j?.error || 'save failed')
      setMessage({ kind: 'success', text: 'Saved. PayoutMonitoring will use these values on next load.' })
    } catch (e) {
      setMessage({ kind: 'error', text: `Save failed: ${e.message}` })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center gap-2 mb-2">
        <ChartBarIcon className="h-6 w-6 text-psg-teal" />
        <h1 className="text-2xl font-semibold text-gray-900">Transaction risk scoring</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Per-payout heuristic used by Payout monitoring. Each factor adds points; the total
        drives the HIGH/MEDIUM/LOW badge and the "review recommended" flag. Lower cutoffs
        in the last section = more sensitive (more reviews).
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm border ${
          message.kind === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
          : message.kind === 'error' ? 'bg-red-50 border-red-200 text-red-900'
          : 'bg-amber-50 border-amber-200 text-amber-900'
        }`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading saved values…</div>
      ) : (
        <div className="space-y-6">
          {GROUPS.map((grp) => (
            <div key={grp.title} className="border rounded-lg p-4 bg-white">
              <div className="text-sm font-semibold text-gray-900 mb-1">{grp.title}</div>
              <div className="text-xs text-gray-500 mb-3">{grp.desc}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {grp.fields.map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-600 mb-1">{label}</label>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={config[key]}
                      onChange={(e) => setField(key, e.target.value)}
                    />
                    <div className="text-[10px] text-gray-400 mt-0.5">default: {String(DEFAULT_TXN_RISK_CONFIG[key])}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-psg-teal text-white rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
              <CheckCircleIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={resetToDefaults}
              disabled={saving}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm text-gray-700 border rounded-md disabled:opacity-40"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset to platform defaults
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
